import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Modal } from "react-bootstrap";
import translations from "../../translation";

const PendingApprovalsTable = ({ language }) => {
  const translate = (key) => translations[language]?.[key] || key;
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingData, setPendingData] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const rowsPerPage = 10;

  useEffect(() => {
    fetchPendingData();
  }, []);

  // API to fetch pending announcements
  const fetchPendingData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_CHECK_USER_API}/api/admin/all-announcement`,
        {
          method: "GET",
        }
      );

      const data = await response.json();
      if (data.success) {
        setPendingData(data.data || []);
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error("Error fetching pending data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Approve API call
  const handleApprove = (taskId) => {
    Swal.fire({
      title: translate("approveConfirmTitle"),
      text: translate("approveConfirmText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: translate("confirmButton"),
    }).then((result) => {
      if (result.isConfirmed) {
        approveTask(taskId);
      }
    });
  };

  const handleReject = (taskId) => {
    Swal.fire({
      title: translate("rejectConfirmTitle"),
      text: translate("rejectConfirmText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: translate("confirmButton"),
    }).then((result) => {
      if (result.isConfirmed) {
        rejectTask(taskId);
      }
    });
  };

  const approveTask = async (taskId) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const formData = new FormData();
      formData.append("id", taskId);
      formData.append("user_id", user.id);
      formData.append("status", "Approved");
      const response = await fetch(
        `${process.env.REACT_APP_CHECK_USER_API}/api/approval`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (data.success) {
        Swal.fire(
          translate("approveSuccessTitle"),
          translate("approveSuccessText"),
          "success"
        );
        fetchPendingData();
      } else {
        Swal.fire(translate("errorTitle"), data.message, "error");
      }
    } catch (error) {
      console.error("Error approving task:", error);
      Swal.fire(translate("errorTitle"), translate("errorText"), "error");
    }
  };

  const rejectTask = async (taskId) => {
    try {
      // Show a prompt to ask for the rejection reason
      const { value: reason } = await Swal.fire({
        title: translate("rejectReasonTitle"),
        input: "textarea",
        inputLabel: translate("reasonLabel"),
        inputPlaceholder: translate("reasonPlaceholder"),
        inputAttributes: {
          "aria-label": translate("reasonAriaLabel"),
        },
        showCancelButton: true,
        confirmButtonText: translate("submitButton"),
        cancelButtonText: translate("cancelButton"),
      });

      // If the user cancels or doesn't provide a reason, do nothing
      if (!reason) {
        Swal.fire(translate("cancelledTitle"), translate("cancelledText"), "info");
        return;
      }

      // Proceed with the rejection
      const user = JSON.parse(localStorage.getItem("user"));
      const formData = new FormData();
      formData.append("id", taskId);
      formData.append("user_id", user.id);
      formData.append("status", "Rejected");
      formData.append("reject_reason", reason);
      const response = await fetch(
        `${process.env.REACT_APP_CHECK_USER_API}/api/approval`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (data.success) {
        Swal.fire(
          translate("rejectSuccessTitle"),
          translate("rejectSuccessText"),
          "success"
        );
        fetchPendingData();
      } else {
        Swal.fire(translate("errorTitle"), data.message, "error");
      }
    } catch (error) {
      console.error("Error rejecting task:", error);
      Swal.fire(translate("errorTitle"), translate("errorText"), "error");
    }
  };

  const handleView = (task) => {
    setSelectedTask(task);
    setModalShow(true);
  };

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = pendingData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(pendingData.length / rowsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleNextPage = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handlePrevPage = () =>
    currentPage > 1 && setCurrentPage(currentPage - 1);

  return (
    <div className="bg-white shadow rounded-lg mb-8">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          {translate("pendingTableTitle")}
        </h3>
      </div>
      {isLoading ? (
        <div className="text-center py-6 text-gray-600">
          {translate("loadingText")}
        </div>
      ) : pendingData.length === 0 ? (
        <div className="text-center py-6 text-gray-600">
          {translate("noDataText")}
        </div>
      ) : (
        <div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate("titleLabel")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate("submittedByLabel")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate("approvedByLabel")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate("dateLabel")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate("statusLabel")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate("actionsLabel")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {row.title || translate("notAvailable")}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {row.sender_name || translate("notAvailable")}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {row.approved_by_name || translate("notAvailable")}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {row.date
                        ? new Date(row.date).toLocaleDateString()
                        : translate("notAvailable")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          row.status === "Pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : row.status === "Approved"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {translate(row.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={() => handleView(row)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-custom hover:text-custom-dark border border-custom rounded-button mr-2"
                      >
                        {translate("viewButton")}
                      </button>
                      {row.status === "Pending" && (
                        <>
                          <button
                            onClick={() => handleApprove(row.id)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-button mr-2"
                          >
                            {translate("approveButton")}
                          </button>
                          <button
                            onClick={() => handleReject(row.id)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-button"
                          >
                            {translate("rejectButton")}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-between items-center px-6 py-3 space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                  currentPage === 1
                    ? "bg-gray-300 text-gray-500"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {translate("prevButton")}
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                  currentPage === totalPages
                    ? "bg-gray-300 text-gray-500"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {translate("nextButton")}
              </button>
            </div>
        </div>
      )}

      {/* Modal */}
      <Modal size="lg" show={modalShow} onHide={() => setModalShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{translate("modalTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>{translate("fieldLabel")}</th>
                <th>{translate("valueLabel")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{translate("titleLabel")}</td>
                <td>{selectedTask?.title || translate("notAvailable")}</td>
              </tr>
              <tr>
                <td>{translate("submittedByLabel")}</td>
                <td>{selectedTask?.sender_name || translate("notAvailable")}</td>
              </tr>
              <tr>
                <td>{translate("approvedByLabel")}</td>
                <td>{selectedTask?.approved_by_name || translate("notAvailable")}</td>
              </tr>
              <tr>
                <td>{translate("dateLabel")}</td>
                <td>
                  {selectedTask?.date
                    ? new Date(selectedTask.date).toLocaleDateString()
                    : translate("notAvailable")}
                </td>
              </tr>
              <tr>
                <td>{translate("statusLabel")}</td>
                <td>{selectedTask?.status || translate("notAvailable")}</td>
              </tr>
            </tbody>
          </table>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PendingApprovalsTable;
